/**
 * A page that is reloading because this browser's identity changed — Login as,
 * Exit, an impersonation the server ended, another tab's switch — must
 * actually go. A "Leave site?" prompt from an unsaved-changes guard would let
 * the user stay on a page whose cookies now belong to someone else, showing the
 * previous person's data under the new person's name with nothing to say so;
 * anything saved there would be saved as the new identity. So such a reload
 * marks itself, and every `beforeunload` guard stands down for it. [HW-872]
 */
let leaving = false;

/**
 * Reloads onto `url` for a change of identity. `replace`, not `assign`: the
 * page being left gets no history entry of its own. Earlier entries of the same
 * document stay in the history; `reloadPagesRestoredFromCache` reloads one the
 * browser brings back from its back/forward cache.
 */
export function leaveForIdentitySwitch(url: string): void {
  leaving = true;
  window.location.replace(url);
}

/** Whether the page is reloading for a change of identity; guards then must not prompt. */
export function isLeavingForIdentitySwitch(): boolean {
  return leaving;
}

/**
 * Public pages that work from the token or slug in their link, not from this
 * browser's sign-in: a candidate's preboarding form, the consent page, the
 * careers site. Another tab's change of identity leaves them as they are — the
 * cookies they would be acting on are not theirs, and a preboarding draft not
 * yet saved would otherwise be lost. [HW-872]
 */
const SIGN_IN_FREE_PAGES = ['/preboarding/', '/consent/', '/careers/'];

export function isSignInFreePage(pathname: string): boolean {
  return SIGN_IN_FREE_PAGES.some((prefix) => pathname.startsWith(prefix));
}

/** The localStorage key the auth store saves the signed-in user under. */
export const AUTH_STORAGE_KEY = 'auth-storage';

/** The id of the user the auth store last saved, in this tab or any other. */
function savedUserId(): string | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) ?? 'null');
    return saved?.state?.user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * A page the browser restores from its back/forward cache comes back with the
 * memory it had when it was left: the signed-in user and the fetched data of
 * whoever was signed in then. When the user saved since is someone else, or the
 * page was itself sent away for a change of identity, that is the previous
 * person's view over the new cookies, so the page reloads.
 * A page restored under the same user keeps its instant restore, and a public
 * page that works from its link is never reloaded. [HW-872]
 *
 * `userInMemory` is the id of the user this page holds in memory.
 */
export function reloadPagesRestoredFromCache(userInMemory: () => string | null): void {
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (!event.persisted || isSignInFreePage(window.location.pathname)) return;
    // The tab that made the switch saved the new user before it left, so its
    // memory matches what is saved; that it was sent away is what tells. [HW-872]
    if (!leaving && userInMemory() === savedUserId()) return;
    // A reload for a change of identity like any other: unsaved-changes
    // prompts must not hold the previous person's view here.
    leaving = true;
    window.location.reload();
  });
}
